import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, Wind, AlertTriangle, ShieldCheck, CloudRain, HardHat } from 'lucide-react';
import { Reveal, Stagger, StaggerItem, CountUp, EASE } from './LandingMotion';

const HAZARDS = [
  { id: 1, type: 'sensor', text: 'Noise level 95 dB', loc: 'Level 2', severity: 'high' },
  { id: 2, type: 'weather', text: 'Wind gusts > 25 mph', loc: 'Site A', severity: 'medium' },
  { id: 3, type: 'permit', text: 'Hot work permit active', loc: 'Zone 4', severity: 'info' },
];

const INPUTS = [
  { label: 'Weather', desc: 'Live conditions', icon: CloudRain },
  { label: 'Site sensors', desc: 'Noise, air, motion', icon: Activity },
  { label: 'Worker reports', desc: 'From the field', icon: HardHat },
];

// Gauge geometry
const R = 80;
const C = 2 * Math.PI * R; // ~502.65
const SCORE = 87;

const sevStyles = {
  high: 'bg-red-500/10 border-red-500/20',
  medium: 'bg-[#FF6A00]/10 border-[#FF6A00]/20',
  info: 'bg-black/[0.04] border-black/10',
};
const sevIconColor = {
  high: 'text-red-400',
  medium: 'text-[#FF6A00]',
  info: 'text-slate-600',
};

export const SafetySection = () => {
  const reduce = useReducedMotion();
  return (
    <section className="py-24 md:py-32 relative bg-white border-t border-black/[0.06]">
      <div className="w-full max-w-[1200px] mx-auto px-6">

        <Reveal className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF6A00]/10 border border-[#FF6A00]/20 text-[#E25C00] text-xs font-mono uppercase tracking-widest mb-6">
            <ShieldCheck size={12} />
            <span>Safety, built in</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1C1C1E] mb-6">
            Real-time safety intelligence
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto font-light">
            Weather, permits, sensors, and worker reports combine into a single, live
            safety picture for every site — so the crew sees the risk before it finds them.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left: Safety score gauge */}
          <Reveal className="relative mx-auto max-w-[420px] w-full" y={28}>
            <div className="absolute inset-8 bg-[#FF6A00]/10 blur-[90px] rounded-full pointer-events-none" />
            <div className="relative bg-white rounded-3xl vh-edge-light p-10">
              <div className="relative aspect-square max-w-[320px] mx-auto">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="14" />
                  <motion.circle
                    cx="100" cy="100" r={R} fill="none" stroke="#FF6A00" strokeWidth="14" strokeLinecap="round"
                    strokeDasharray={C}
                    initial={{ strokeDashoffset: C }}
                    whileInView={{ strokeDashoffset: C * (1 - SCORE / 100) }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: 'easeOut' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-7xl font-bold text-[#1C1C1E] tracking-tighter tabular-nums vh-nums">
                    <CountUp value={SCORE} duration={1.6} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#FF6A00] font-bold mt-1">Safety Score</div>
                  <div className="flex items-center gap-2 mt-4 px-3 py-1 bg-black/[0.04] rounded-full border border-black/10 text-xs text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live
                  </div>
                </div>
              </div>

              <Stagger className="grid grid-cols-3 gap-3 mt-8" stagger={0.08} delayChildren={0.2}>
                {INPUTS.map((s) => (
                  <StaggerItem key={s.label} y={14} className="rounded-xl p-3 bg-black/[0.04] border border-black/[0.06] flex flex-col items-center text-center">
                    <s.icon size={18} className="text-[#FF6A00] mb-2" />
                    <div className="text-xs font-semibold text-[#1C1C1E]">{s.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{s.desc}</div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </Reveal>

          {/* Right: Live hazard feed */}
          <Reveal className="bg-white rounded-3xl vh-edge-light p-6 md:p-8" y={28} delay={0.08}>
            <div className="flex items-center justify-between mb-6 border-b border-black/[0.06] pb-4">
              <h3 className="text-lg font-bold text-[#1C1C1E]">Live Hazard Feed</h3>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Monitoring</span>
              </div>
            </div>

            <Stagger className="space-y-3" stagger={0.1} delayChildren={0.15}>
              {HAZARDS.map((item) => (
                <StaggerItem
                  key={item.id}
                  y={16}
                  whileHover={reduce ? undefined : { y: -2 }}
                  transition={{ duration: 0.2, ease: EASE }}
                  className={`p-4 rounded-xl border ${sevStyles[item.severity]}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      {item.type === 'weather' && <Wind size={16} className={sevIconColor[item.severity]} />}
                      {item.type === 'sensor' && <AlertTriangle size={16} className={sevIconColor[item.severity]} />}
                      {item.type === 'permit' && <Activity size={16} className={sevIconColor[item.severity]} />}
                      <span className="text-sm font-medium text-[#1C1C1E]">{item.text}</span>
                    </div>
                    <span className="text-xs font-mono text-slate-500">Now</span>
                  </div>
                  <div className="text-xs text-slate-500 pl-[26px] mt-1">{item.loc}</div>
                </StaggerItem>
              ))}
              <StaggerItem y={16} className="p-4 rounded-xl bg-black/[0.03] border border-black/[0.06]">
                <div className="text-sm text-slate-500">All clear on remaining zones — last scan just now.</div>
              </StaggerItem>
            </Stagger>
          </Reveal>

        </div>
      </div>
    </section>
  );
};
